/**
 * Cost tracking API service.
 *
 * This module provides functions for fetching LLM cost data and managing pricing.
 */

import apiClient from './api';

/**
 * Get cost summary for a given period.
 * @param {string} period - 'day', 'week', or 'month'
 * @returns {Promise<Object>} Cost summary data
 */
export const getCostSummary = async (period = 'month') => {
  const response = await apiClient.get(`/api/admin/costs/summary?period=${period}`);
  return response.data;
};

/**
 * Get daily cost data for charting.
 * @param {number} days - Number of days of history
 * @returns {Promise<Array>} Daily cost data
 */
export const getDailyCosts = async (days = 30) => {
  const response = await apiClient.get(`/api/admin/costs/daily?days=${days}`);
  return response.data;
};

/**
 * Get detailed cost breakdown.
 * @param {string} period - 'day', 'week', or 'month'
 * @returns {Promise<Object>} Cost breakdown by provider, model, and request type
 */
export const getCostBreakdown = async (period = 'month') => {
  const response = await apiClient.get(`/api/admin/costs/breakdown?period=${period}`);
  return response.data;
};

/**
 * Get list of models with unknown pricing.
 * @returns {Promise<Array>} List of unknown pricing entries
 */
export const getUnknownPricing = async () => {
  const response = await apiClient.get('/api/admin/costs/unknown-pricing');
  return response.data;
};

/**
 * Get all pricing configurations.
 * @returns {Promise<Array>} List of pricing entries
 */
export const getPricing = async () => {
  const response = await apiClient.get('/api/admin/pricing');
  return response.data;
};

/**
 * Create a new pricing entry.
 * @param {Object} pricing - Pricing data
 * @returns {Promise<Object>} Created pricing entry
 */
export const createPricing = async (pricing) => {
  const response = await apiClient.post('/api/admin/pricing', pricing);
  return response.data;
};

/**
 * Update an existing pricing entry.
 * @param {number} id - Pricing entry ID
 * @param {Object} pricing - Updated pricing data
 * @returns {Promise<Object>} Updated pricing entry
 */
export const updatePricing = async (id, pricing) => {
  const response = await apiClient.put(`/api/admin/pricing/${id}`, pricing);
  return response.data;
};

/**
 * Delete a pricing entry.
 * @param {number} id - Pricing entry ID
 * @returns {Promise<Object>} Success response
 */
export const deletePricing = async (id) => {
  const response = await apiClient.delete(`/api/admin/pricing/${id}`);
  return response.data;
};

/**
 * Recalculate costs for a date range.
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Recalculation result
 */
export const recalculateCosts = async (startDate, endDate) => {
  const response = await apiClient.post('/api/admin/pricing/recalculate', {
    start_date: startDate,
    end_date: endDate
  });
  return response.data;
};
